import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import ReactPaginate from 'react-paginate';
import { useSearchParams } from 'react-router-dom';
import { FILTER_IDS, ZERO_INDEX_EQUALIZER } from '@src/js/constants';

/**
 * Renders a page number selector (using ReactPaginate) with an overall
 * page count beneath it.
 */
const Pagination = ({
  pageLabel,
  ofLabel,
  breakLabel,
  totalPages,
  pageRangeDisplayed,
  marginPagesDisplayed,
  paginationHandler,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const pageNumOneBased = searchParams.get(FILTER_IDS.PAGE) ?? 1;
  const pageNumZeroBased = pageNumOneBased - ZERO_INDEX_EQUALIZER;

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    setSearchParams(searchParams);
  }, [window.location.search]);

  const handlePagination = (event) => {
    const pageNumZeroBased = event.selected;
    const pageNumOneBased = pageNumZeroBased + ZERO_INDEX_EQUALIZER;

    setSearchParams((prev) => {
      prev.set(FILTER_IDS.PAGE, pageNumOneBased);
      return prev;
    });

    window.scroll({ top: 0, left: 0, behavior: 'smooth' });

    if (paginationHandler) {
      paginationHandler(pageNumZeroBased);
    }
  };

  return (
    <>
      <ReactPaginate
        breakLabel={breakLabel}
        nextLabel=''
        onPageChange={handlePagination}
        pageRangeDisplayed={pageRangeDisplayed}
        marginPagesDisplayed={marginPagesDisplayed}
        pageCount={totalPages}
        previousLabel=''
        forcePage={pageNumZeroBased}
      />
      <p className='page-count-text'>{pageLabel} {pageNumOneBased} {ofLabel} {totalPages}</p>
    </>
  );
}

Pagination.propTypes = {
  /**
   * the label for 'Page' in 'Page 1 of 3'
   */
  pageLabel: PropTypes.string.isRequired,
  /**
   * the label for 'of' in 'Page 1 of 3'
   */
  ofLabel: PropTypes.string.isRequired,
  /**
   * the label for the ellipsis in the page selector
   */
  breakLabel: PropTypes.string,
  /**
   * the total number of pages
   */
  totalPages: PropTypes.number.isRequired,
  /**
   * the range of pages displayed
   */
  pageRangeDisplayed: PropTypes.number,
  /**
   * the number of pages to display for margins
   */
  marginPagesDisplayed: PropTypes.number,
  /**
   * the callback function for when the page is changed (accepts the current page number)
   */
  paginationHandler: PropTypes.func,
};

Pagination.defaultProps = {
  breakLabel: '...',
  pageRangeDisplayed: 5,
  marginPagesDisplayed: 1,
};

export default Pagination;
